import { Color, NumberWithUnit, StringUtil } from "@mcleod/core";
import { ButtonStyles } from "../button/ButtonStyles";
import { Component } from "../../base/Component";
import { ComponentListenerDefs } from "../../base/ComponentListenerDefs";
import { Cursor } from "../../base/Cursor";
import { DomEvent } from "../../events/DomEvent";
import { DropdownItem } from "../textbox/DropdownItem";
import { Event } from "../../events/Event";
import { UnitInputField } from "./UnitInputField";
import { UnitInputFieldStyles } from "./UnitInputFieldStyles";
import { UnitInputFieldInnerTextbox } from "./UnitInputFieldInnerTextbox";
import { TextboxItemsSource } from "../textbox/Textbox";
import { TextboxProps } from "../textbox/TextboxProps";
import { TooltipDisplayer } from "../../page/tooltips/TooltipDisplayer";
import { ValidationResult } from "../../base/ValidationResult";

export class TextboxUnit extends UnitInputFieldInnerTextbox {
    constructor(props: Partial<TextboxProps>, unitInputField: UnitInputField) {
        super(props, unitInputField);
        this.displayInputAsButton();
        this.makeNotFocusable();
        this.syncHoverColors();
        this._inputDiv.classList.add(UnitInputFieldStyles.textboxUnit);
    }

    override getDataValue(): string {
        const boundValue = this.getRelevantModelRow()?.get(this.unitInputField.field, null) as NumberWithUnit;
        if (boundValue?.unit != null) {
            return boundValue.unit.abbreviation;
        }
        return this.getBasicValue();
    }

    private displayInputAsButton() {
        this._input.setAttribute("readonly", "true");
        this._inputDiv.classList.add(UnitInputFieldStyles.textboxUnit_hoverEnabled);
    }

    // This should go away when we decide to make the unit dropdown focusable
    private makeNotFocusable() {
        this._element.tabIndex = -1;
        this._input.tabIndex = -1;
        this._inputDiv.tabIndex = -1;
        this["isFocusable"] = (): boolean => {
            return false;
        }
    }

    override get items(): TextboxItemsSource {
        return super.items;
    }

    override set items(value: TextboxItemsSource) {
        super.items = value;
        // Have to wait and do this after items are assigned, since the dropdown button won't be created until that time
        this.makeEntireComponentClickable();
    }

    // Override so that the dropdown's display value shows a combination of the display value and the db value
    override resolveItems(includeBlankIfNeeded = false): DropdownItem[] {
        const result = super.resolveItems(includeBlankIfNeeded);
        return result.map(item => {
            if (item.getAddlValue("finalizedDisplayValue") !== true) {
                if (item.value !== "<blank>") {
                    item.displayValue = `${item.displayValue} (${item.value})`;
                }
                item.setAddlValue("finalizedDisplayValue", true);
            }
            return item;
        });
    }

    protected override get componentToFocusOnDropdownClose(): Component {
        return this.unitInputField;
    }

    // Override so we can display only part of the selected item's display value
    override _setText(value: string, event: Event | DomEvent): void {
        if (this.selectedItem != null && value.startsWith(this.selectedItem.displayValue)) {
            value = value.replace(this.selectedItem.displayValue, this.selectedItem.value);
        }
        super._setText(value, event);
    }

    public get color(): Color {
        return super.color;
    }

    public set color(value: Color) {
        super.color = value;
        if (this.buttonHandler?.button != null) {
            this.buttonHandler.button.color = value;
        }
    }

    protected override getColorTargets(): HTMLElement[] {
        return [ this._inputDiv ];
    }

    public override get backgroundColor(): Color {
        return super.backgroundColor;
    }

    public set backgroundColor(value: Color) {
        super.backgroundColor = value;
        this.syncHoverColors();
    }

    protected override getBackgroundColorTargets(): HTMLElement[] {
        return [ this._inputDiv ];
    }

    private syncHoverColors() {
        if (StringUtil.isEmptyString(this.backgroundColor)) {
            this._inputDiv.classList.remove(UnitInputFieldStyles.textboxUnit_hoverEnabledWithBackgroundColor);
        } else {
            this._inputDiv.classList.add(UnitInputFieldStyles.textboxUnit_hoverEnabledWithBackgroundColor);
        }
    }

    public get enabled(): boolean {
        return this.unitInputField?.unitDisabledByServer !== true && super.enabled;
    }

    public set enabled(value: boolean) {
        super.enabled = value;
    }

    override _applyEnabled(value: boolean): void {
        super._applyEnabled(value);
        if (this._input == null) {
            // Still constructing the object, skip the rest of the settings for now
            return;
        }
        if (value) {
            this.backgroundColor = this.backgroundColor;
            this.color = this.color;

            this._inputDiv.style.borderBottomColor = "";
            this._inputDiv.style.borderRightColor = "";
            this._inputDiv.style.borderTopColor = "";
        }
        this.syncCursor();
    }

    /**
     * - Assign the same click listener in the Textbox's button handler to this entire component.
     * - Defeat the normal dropdown button's hover styling & ripple effect.
     */
    private makeEntireComponentClickable() {
        const button = this.buttonHandler.button;
        button["ripple"] = (): Promise<void> => { return null; };
        button.removeClass(ButtonStyles.hoverEnabled);
        button.removeClass(ButtonStyles.focusEnabled);
        button.removeAllEventListeners(ComponentListenerDefs.click);
        button.color = this.color;
        this.addClickListener(this.prepParentComponent);
        this.addClickListener(this.toggleDropdown);
        this.syncCursor();
    }

    private syncCursor() {
        const cursor = (this.hasClickListeners() && this.enabled) ? Cursor.POINTER : null;
        this.cursor = cursor;
        this._input.style.cursor = cursor;
    }

    public override getEventTarget(): HTMLElement {
        return this._element;
    }

    private prepParentComponent = () => {
        // Dismiss any tooltips when the 'button' is clicked.  This is helpful because it will be common for the
        // UnitInputField's tooltip to be displayed before the user clicks the 'button'...we'll want to dismiss that
        // tooltip before shoing the dropdown to the user.
        TooltipDisplayer.removeAll();

        // Show focus border around entire UnitInputField
        this.unitInputField.updateBordersForFocus(true);
    }

    cleanupDropdown(): void {
        super.cleanupDropdown();
        this.unitInputField.updateBordersForFocus(false);
    }

    override validate(checkRequired: boolean, showErrors: boolean = true): ValidationResult[] {
        const result = super.validate(checkRequired, showErrors);
        if (result != null) {
            for (let x=0 ; x<result.length ; x++) {
                if (result[x]?.validationMessage?.toLowerCase() === "this field is required") {
                    result[x].validationMessage = "The unit is required";
                }
            }
        }
        return result;
    }
}
